import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@quebon/ui/components/dialog'
import { toast } from 'sonner'
import { Loader2 } from 'lucide-react'
import { phoneApi } from '@/api/phone'
import { isValidKrPhone, normalizeKrPhone } from '@/utils/phoneFormat'
import { getApiErrorMessage } from '@/utils/apiError'

const COUNTRY_CODE = '+82'

interface Props {
  isOpen: boolean
  mode: 'register' | 'change'
  onClose: () => void
  onUpdated: () => void
}

type Step = 'phone' | 'code'

export function PhoneVerifyModal({ isOpen, mode, onClose, onUpdated }: Props) {
  const { t } = useTranslation(['admin', 'common'])
  const [step, setStep] = useState<Step>('phone')
  const [phone, setPhone] = useState('')
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [resendIn, setResendIn] = useState(0)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    if (isOpen) {
      setStep('phone')
      setPhone('')
      setCode('')
      setError(null)
      setBusy(false)
      setResendIn(0)
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current)
    }
  }, [isOpen])

  const startResendTimer = () => {
    setResendIn(60)
    if (timerRef.current) clearInterval(timerRef.current)
    timerRef.current = setInterval(() => {
      setResendIn((s) => {
        if (s <= 1) {
          if (timerRef.current) clearInterval(timerRef.current)
          return 0
        }
        return s - 1
      })
    }, 1000)
  }

  const onSend = async () => {
    const normalized = normalizeKrPhone(phone)
    if (!isValidKrPhone(normalized)) {
      setError(t('admin:profile.phone.invalidFormat'))
      return
    }
    setBusy(true)
    setError(null)
    try {
      await phoneApi.sendCode({ countryCode: COUNTRY_CODE, phoneNumber: normalized })
      setStep('code')
      startResendTimer()
    } catch (e) {
      setError(getApiErrorMessage(e, t))
    } finally {
      setBusy(false)
    }
  }

  const onVerifyAndUpdate = async () => {
    const normalized = normalizeKrPhone(phone)
    setBusy(true)
    setError(null)
    try {
      const verifyRes = await phoneApi.verifyCode({ countryCode: COUNTRY_CODE, phoneNumber: normalized, code })
      const vt = verifyRes.data.data!.verificationToken
      await phoneApi.updatePhone({ countryCode: COUNTRY_CODE, phoneNumber: normalized, verificationToken: vt })
      toast.success(t('admin:profile.phone.toastUpdated'))
      onUpdated()
      onClose()
    } catch (e) {
      setError(getApiErrorMessage(e, t))
    } finally {
      setBusy(false)
    }
  }

  const title = mode === 'register'
    ? t('admin:profile.phone.modalTitleRegister')
    : t('admin:profile.phone.modalTitleChange')

  return (
    <Dialog open={isOpen} onOpenChange={(o) => { if (!o) onClose() }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        {step === 'phone' && (
          <div className="space-y-3">
            <label htmlFor="lc-phone" className="block text-sm font-medium text-gray-700">
              {t('admin:profile.phone.phoneLabel')}
            </label>
            <input
              id="lc-phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              inputMode="numeric"
              placeholder={t('admin:profile.phone.phonePlaceholder')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none"
            />
            {error && <p className="text-sm text-red-500">{error}</p>}
            <button
              onClick={onSend}
              disabled={busy || !phone}
              className="w-full px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}
              {t('admin:profile.phone.sendCodeButton')}
            </button>
          </div>
        )}

        {step === 'code' && (
          <div className="space-y-3">
            <label htmlFor="lc-code" className="block text-sm font-medium text-gray-700">
              {t('admin:profile.phone.codeLabel')}
            </label>
            <input
              id="lc-code"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              inputMode="numeric"
              maxLength={6}
              placeholder={t('admin:profile.phone.codePlaceholder')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg tracking-[0.3em] focus:ring-2 focus:ring-primary-500 outline-none"
            />
            {error && <p className="text-sm text-red-500">{error}</p>}
            <button
              onClick={onVerifyAndUpdate}
              disabled={busy || code.length !== 6}
              className="w-full px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}
              {t('admin:profile.phone.applyButton')}
            </button>
            <button
              onClick={onSend}
              disabled={busy || resendIn > 0}
              className="w-full text-center text-sm text-primary-600 hover:underline disabled:opacity-50"
            >
              {resendIn > 0
                ? t('admin:profile.phone.resendIn', { seconds: resendIn })
                : t('admin:profile.phone.resendButton')}
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
